import './InsightsComplete.scss';

import * as React from 'react';
import { useMemo, useState } from 'react';

import SlDetails from '@shoelace-style/shoelace/dist/react/details';
import { max } from 'date-fns';

import { translate } from '~l10n/client';
import { Divider } from '~shared/components/Divider';
import { IconExclamationTriangle } from '~shared/components/icons/large';
import { IconChevron } from '~shared/components/icons/micro';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { useResultState } from '~teacher/components/ActivityResult/ActivityResultProvider';
import { InsightsBorderContainer } from '~teacher/components/ResultsGrid/Insights/layout/InsightsBorderContainer';
import { QuestionScore } from '~teacher/models/activities';

import {
  ActivityInsights,
  Insights,
} from '../../../../api/activities/insights/insightSchema';
import { InsightsGenerateQuestionsCTA } from './GenerateQuestions/InsightsGenerateQuestionsCTA';
import { InsightsCompleteDetail } from './InsightsCompleteDetails';
import {
  InsightsGenerateInsightsButton,
  useInsightsGenerateInsightsButton,
} from './InsightsGenerateInsightsButton';
import { InsightsGenerationBlockerDialog } from './InsightsGenerationBlockerDialog';

function checkIfInsightsAreOutdated(
  insights: ActivityInsights,
  questionScores: Array<QuestionScore>
): boolean {
  const latestInsightsDate = new Date(insights.updatedDate);

  const timestamps = questionScores.flatMap((questionScore) =>
    Object.values(questionScore.studentScores ?? {}).map(
      (studentScore) => new Date(studentScore.updatedDate)
    )
  );

  const latestGradingDate = max(timestamps);

  // If the latest grading date is after the latest insights date, insights are outdated
  return latestGradingDate > latestInsightsDate;
}

interface InsightsCompletedHeaderProps {
  activityInstanceId: number;
  chevronDirection: 'up' | 'down';
  insightCount: number;
  insights: ActivityInsights;
  averageScoreRate?: number | null;
}

function InsightsCompletedHeader({
  activityInstanceId,
  chevronDirection,
  insightCount,
  insights,
  averageScoreRate,
}: InsightsCompletedHeaderProps) {
  const {
    isLoading,
    onClickCapture,
    showNoInsightsDialog,
    setShowNoInsightsDialog,
  } = useInsightsGenerateInsightsButton({
    activityInstanceId,
    averageScoreRate,
  });

  const { questionScores } = useResultState();

  const isOutdated = useMemo(() => {
    return checkIfInsightsAreOutdated(insights, Object.values(questionScores));
  }, [insights, questionScores]);

  return (
    <>
      <div slot="summary" className="flex items-center justify-between w-full">
        <div className="flex items-center flex-shrink pointer-events-auto gap-1">
          <span className="font-600 font-san text-base leading-8 text-blue-900">
            {translate('{0} Areas of Improvement').format(insightCount)}
          </span>

          <div className="flex items-center text-cyan-600">
            <IconChevron direction={chevronDirection} />
          </div>

          {isOutdated && (
            <TooltipV2
              label={translate(
                'Your insights may be out of date. Reanalyze to include the latest grades.'
              )}
              maxContentWidth={360}
              mode="click"
            >
              <div className="inline-flex ml-2">
                <IconExclamationTriangle className="text-amber-700 h-8 w-8" />
              </div>
            </TooltipV2>
          )}
        </div>

        {isOutdated && (
          <div className="flex items-center flex-shrink gap-4 pointer-events-auto">
            <InsightsGenerateInsightsButton
              isLoading={isLoading}
              onClickCapture={onClickCapture}
            />
          </div>
        )}
      </div>

      <InsightsGenerationBlockerDialog
        open={showNoInsightsDialog}
        onClose={() => setShowNoInsightsDialog(false)}
      />
    </>
  );
}

interface InsightsCompletedProps {
  insights: ActivityInsights;
  activityInstanceId: number;
  questionIdToNumberMap: Map<string, number>;
  participantUuidToNameMap: Map<string, string>;
  averageScoreRate?: number | null;
  defaultOpen?: boolean;
}

export function InsightsCompleted({
  insights,
  activityInstanceId,
  questionIdToNumberMap,
  participantUuidToNameMap,
  averageScoreRate,
  defaultOpen = false,
}: InsightsCompletedProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const chevronDirection = isOpen ? 'down' : 'up';
  const insightDetails: Array<Insights> = insights.insights.insights;

  return (
    <InsightsBorderContainer>
      <SlDetails
        className="insights-complete"
        open={isOpen}
        onSlShow={() => setIsOpen(true)}
        onSlHide={() => setIsOpen(false)}
      >
        <InsightsCompletedHeader
          activityInstanceId={activityInstanceId}
          chevronDirection={chevronDirection}
          insightCount={insightDetails.length}
          insights={insights}
          averageScoreRate={averageScoreRate}
        />
        <Divider />
        {insightDetails.map(
          ({ description, questionIds, studentIds, topic }, index) => {
            const questionNumbers = questionIds
              .map((id) => questionIdToNumberMap.get(id) ?? null)
              .filter((num) => num !== null);

            const participantNames = studentIds
              .map((uuid) => participantUuidToNameMap.get(uuid) ?? null)
              .filter((name) => name !== null)
              .sort((a, b) => a.localeCompare(b));

            return (
              <React.Fragment key={index}>
                <div className="py-5">
                  <InsightsCompleteDetail
                    topic={topic}
                    description={description}
                    questionNumbers={questionNumbers}
                    participantNames={participantNames}
                    insightNumber={index + 1}
                  />
                </div>
                <Divider />
              </React.Fragment>
            );
          }
        )}
        <InsightsGenerateQuestionsCTA
          activityInstanceId={activityInstanceId}
          insights={insights}
        />
      </SlDetails>
    </InsightsBorderContainer>
  );
}
